<script setup>
// 비전 2027 — 반응형으로 옮긴 마지막 섹션.
//
// 원본: Figma 홈(ver1) 391:281 (1920x1116), 배경 #fffdfa
//   머리말 x200   눈썹 y150 / 제목 y222·y288 (56px) / 본문 y394 (20px 투명도 50%)
//   가로선 y247, y460   세로선 x452, x960, x1470 (섹션 높이 전체)
//   카드   1: x197 y809 510x307   2: x704 y694 511x422   3: x1212 y381 511x735
//          셋 다 아래끝이 1116 에서 맞고 높이만 올라간다.
//   점 5개 (1470,247) (960,460) (452,811) (960,697) (1470,384)
//   배경 곡선 4개 — 화면 밖까지 뻗는 장식
//
// ── 어떻게 옮겼나 ────────────────────────────────────────────────────
// 가로만 반응하고 세로는 고정이다(토스와 같은 방식).
//   가로: 모든 x 와 폭을 1920 기준 비율(%)로 바꿨다.
//   세로: y 와 높이는 원본 px 그대로다. 그래서 글자 크기도 원본 그대로 둘 수 있다.
//
// 점 5개는 전부 "선과 선" 또는 "선과 카드 윗변"이 만나는 자리다. 곡선 위가 아니다.
// 선·카드·점이 모두 같은 층에서 같은 비율로 움직이므로 어느 폭에서도 교차점이 맞는다.
// 곡선은 아무것과도 맞물리지 않는 장식이라 따로 통째로 줄인다.
//
// 가로 배율 k = (창폭 - 40) / 1526. 1526 은 카드 3장이 차지하는 폭(197~1723)이고,
// 40 은 좌우 여백 20 씩이다. 그래서 카드 양끝이 언제나 여백 20 에 딱 선다.
// 상한은 1 — 1920 을 넘으면 더 키우지 않는다. 1920·2560·3840 이 완전히 같아야 한다.
//
// 주의: 글자 두 덩이가 나란히 놓이는 자리(20|조, 367|조)는 각각을 %로 놓으면 안 된다.
// 글자 폭은 px 고정인데 사이 좌표만 줄어들어 좁은 화면에서 겹친다. 그래서 그 쌍은
// 바깥 상자 하나만 %로 놓고 안쪽은 붙여 둔다(flex).
//
// 좁아지면 글이 접힌다(사용자 승인). 카드 안 글 상자에 폭을 주어 카드 안에서 접히게 했다.
//   1827 미만에서 카드2 부제가, 1100 언저리부터 카드3 라벨이 두 줄이 된다.
//   원본은 둘 다 한 줄이다. 세로에 여유가 있어 접혀도 아래 내용과 부딪히지 않는다.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import VisionBackdrop from '@/components/home2/VisionBackdrop.vue'
import {
  SCENE_REVEAL_DELAY_MS,
  SCENE_APPEAR_MS,
  VISION_BUILD_STAGGER_MS,
  SCENE_TEXT_SUB_MS,
  VISION_CARD_RISE_MS,
  SCENE_TEXT_STAGGER_MS,
  sceneTextReveal,
} from '@/data/homeMotionTiming.js'
import imgRectangle240649922 from '@/assets/figma/91cc8a7a4339412c6a9fcdb56d5cae083168bed7.png'
import imgVector17 from '@/assets/figma/db048ab25927ed21e0c4e2135250100f20bb58b5.svg'
import imgVector19 from '@/assets/figma/7841a1fc100b4359cdf2fbdc5cfe900776655fce.svg'
import imgVector23 from '@/assets/figma/1d44b2f63af69735ebf97ac5f5779ded00ce7980.svg'
import imgVector24 from '@/assets/figma/ab5865e0cb10287cd9bdad9b547ec3a48e00afb0.svg'
import imgEllipse88 from '@/assets/figma/6fb25cebb45b028067c9c02444067e3fb0c1b81a.svg'
import imgEllipse76 from '@/assets/figma/04fc1c2fc7bc83b87c5b3b2605aa716e8d33660e.svg'
// 화살표(391:340)는 Figma 에서 비전 프레임 밖 형제 노드라 코드젠 출력에 없다.
import imgUnion from '@/assets/figma/f756e8b2eb6afb00e8161d9701649f55ce4a7fc4.svg'

const props = defineProps({
  // 현재 스토리보드 프레임 번호 (홈1-N)
  step: { type: Number, default: 47 },
  // 배경을 끄고 나온다. 섹션 뒤에 깔린 디졸브 배경이 색을 맡는다.
  bare: { type: Boolean, default: false },
})

const DESIGN_W = 1920
const CONTENT_W = 1526 // 카드 3장이 차지하는 폭 (197~1723)
const H = 1116
// 캔버스는 눈썹이 y150 에서 시작한다. 원본이 프레임 안에 둔 여백인데, 배경을 뺀
// 뒤로는 그냥 빈 자리로 보인다. 층을 그만큼 올리고 섹션 높이에서도 뺀다(2026-09-26).
const TOP_TRIM = 150
// 다른 섹션과 같은 기준선. 헤더의 1530 에 좌우 여백 20 씩을 더한 값이다.
const GUTTER_W = 1570

// 1920 기준 x 를 구조 층 안의 비율로.
const X = (px) => `${(px / DESIGN_W) * 100}%`

const root = ref(null)
const k = ref(1)
let observer = null

// 그림 층(카드 + 선 + 점)을 다른 섹션과 같은 기준선에 맞추는 배율.
// 1920 캔버스를 1570 에 넣으므로 0.818 이다.
//
// 줄임(--fit)을 되돌리지 않는다. 한때 GUTTER_W / (DESIGN_W * f) 로 나눠 뒀는데,
// 그러면 줄임이 상쇄돼 이 섹션만 절대 안 줄어든다 - 높이가 790 에 고정이라
// 볼 수 있는 자리가 그보다 좁은 창에서는 한 화면에 안 들어갔다
// (2026-09-27 제보: 1366x600 에서 자리 709 대 높이 790).
//
// 이제 다른 섹션과 같이 줄어든다. 대신 줄어들면 폭도 같이 좁아진다 - 고정 비율
// 캔버스라 세로만 줄일 수가 없다. 다른 섹션은 상자 폭을 --fit 으로 나눠 1530 을
// 지키지만 여기는 그림이라 그 수가 통하지 않는다.
const measure = () => {
  if (!root.value) return
  k.value = GUTTER_W / DESIGN_W
}


onMounted(() => {
  measure()
  observer = new ResizeObserver(measure)
  if (root.value) observer.observe(root.value)
  window.addEventListener('resize', measure)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
  window.removeEventListener('resize', measure)
})

// ── 연출 ─────────────────────────────────────────────────────────────
// 원본 그대로다. 헤더와 카드가 한 정지점(홈1-42)에서 이어서 진행된다.
//
// 이동은 transform 이 아니라 CSS translate 속성으로 준다.
// -translate-x-1/2 로 중앙정렬된 요소(교차점 5개)가 있는데, transform 을 인라인으로
// 쓰면 그 클래스가 통째로 덮어써진다. translate 는 transform 과 별도로 합성된다.
const SECTION_FRAME = 42
const headOn = computed(() => props.step >= SECTION_FRAME)

const base = (on, order, stagger, rise = 0, duration = SCENE_APPEAR_MS) => ({
  opacity: on ? 1 : 0,
  translate: on ? 'none' : `0 ${rise}px`,
  transitionProperty: 'opacity, translate',
  transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
  transitionDuration: `${duration}ms`,
  transitionDelay: on ? `${SCENE_REVEAL_DELAY_MS + order * stagger}ms` : '0ms',
})

// 헤더: 뱃지 → 헤드라인 2줄(아래에서 위로 80px) → 본문
// 두 단계뿐이다 - 0 은 뱃지+헤드라인 두 줄, 1 은 본문.
// 여기 글은 절대배치라 인라인 transform 을 못 쓴다(translate 속성으로 준다).
const head = (order) => sceneTextReveal(headOn.value, order, { translateProp: true })

// 카드와 연결선: 6단계로 쌓인다. 카드는 아래에서 올라오고(rise), 선·화살표는 제자리 페이드.
const build = (beat, rise = 0) => ({
  ...base(headOn.value, beat, VISION_BUILD_STAGGER_MS, rise, rise ? VISION_CARD_RISE_MS : SCENE_APPEAR_MS),
  transitionDelay: headOn.value
    ? `${SCENE_TEXT_SUB_MS + beat * VISION_BUILD_STAGGER_MS}ms`
    : '0ms',
})

// 가로선 2개와 그 위의 점 2개는 '상시' 였다(DOTS 의 beat null). 그런데 비전으로
// 내려오는 동안 흰 바탕에 선과 하이라이트만 떠 보였다(2026-10-16 지적).
// 배경 곡선(SceneBackdrop 의 decoFrame 42 · 700ms)과 **같은 시점·같은 속도**로 묶는다.
// build() 와 달리 clip/translate 없이 투명도만 쓴다 - 배경처럼 떠오르면 된다.
const AMBIENT_FADE_MS = 700
const ambient = computed(() => ({
  opacity: headOn.value ? 1 : 0,
  transition: `opacity ${AMBIENT_FADE_MS}ms ease`,
}))

// 카드 배경. 원본 값 그대로다.
const CARD1_BG = {
  backgroundImage:
    'linear-gradient(148.60130222717922deg, rgba(255, 237, 199, 0.8) 11.231%, rgba(255, 248, 230, 0.8) 119.93%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.8) 100%)',
}
const CARD2_BG = {
  backgroundImage:
    'linear-gradient(130.5127481286724deg, rgba(255, 249, 235, 0.8) 9.3674%, rgba(255, 251, 243, 0.8) 101.83%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.8) 100%)',
}
const CARD3_GLOW = {
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 505 732' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='0.6000000238418579'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(-33.092 -64.169 44.394 -18.4 330.92 641.69)'><stop stop-color='rgba(255,182,22,1)' offset='0'/><stop stop-color='rgba(255,187,40,1)' offset='0.125'/><stop stop-color='rgba(255,193,58,1)' offset='0.25'/><stop stop-color='rgba(255,204,94,1)' offset='0.5'/><stop stop-color='rgba(255,216,130,1)' offset='0.75'/><stop stop-color='rgba(255,227,166,1)' offset='1'/></radialGradient></defs></svg>\")",
}

// 세로선 3개: [x, 등장 순서, 이미지]
const VLINES = [
  [1470, 5, imgVector19],
  [960, 3, imgVector23],
  [452, 1, imgVector24],
]
// 교차점 5개: [x, y(점 상자 기준), 등장 순서 — null 이면 상시]
const DOTS = [
  [1470, 242, null],
  [960, 455, null],
  [452, 806, 0],
  [960, 692, 2],
  [1470, 379, 4],
]
const SOURCES = ['씨티뱅크 글로벌', '하나은행', '한국 IR 협의회']
</script>

<template>
  <!-- 곡선과 섹션을 한 상자에 담는다. 곡선은 <section> 밖이다 - 거기 걸린
       overflow-hidden(구조 층 위쪽 TOP_TRIM 을 자르는 용도)에 걸리면 상자
       모서리에서 선이 끊긴다. -->
  <div class="relative w-full">
    <!-- 배경 곡선. 2026-09-26 에 SceneBackdrop 으로 뺐다가 2026-10-16 에 되돌렸다.
         뺐던 이유는 "콘텐츠 상자에 잘리고 구조 층과 배율이 달라 따로 논다" 였는데,
         그 배율 차이(0.9896 대 0.8177 = 1.21배) 때문에 하이라이트 점이 배경
         차트 라인에서 벗어났다. 이제 **구조 층과 같은 상자**(1920x1116, 배율 k,
         top -TOP_TRIM*k)에 얹으므로 점·세로선·가로선·카드와 한 번에 맞는다.
         안쪽 VisionBackdrop 은 scale 1 로 둔다 - 이 상자가 이미 줄여 놓았다.
         등장은 가로선·점과 같은 ambient 로 묶는다. -->
    <div
      aria-hidden
      class="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden"
      :style="{ top: `${-TOP_TRIM * k}px` }"
    >
      <div
        class="absolute left-1/2 top-0 origin-top"
        :style="[
          {
            width: `${DESIGN_W}px`,
            height: `${H}px`,
            transform: `translateX(-50%) scale(${k})`,
          },
          ambient,
        ]"
      >
        <VisionBackdrop :scale="1" />
      </div>
    </div>

  <section
    ref="root"
    class="relative flex w-full flex-col overflow-hidden"
    :class="{ 'bg-[#fffdfa]': !bare }"
    :style="{ height: `${(H - TOP_TRIM) * k}px` }"
    data-node-id="391:281"
  >
    <!-- 배경 곡선은 여기 없다. 배경이라 SceneBackdrop 으로 옮겼다
         (2026-09-26: 배경과 콘텐츠를 나눴다). 섹션 안에 두면 콘텐츠 상자에 잘리고,
         구조 층과 배율이 달라(bgK vs k) 카드와 따로 논다. VisionBackdrop.vue 에 있다. -->

    <!-- 가로선 2개. 창을 끝에서 끝까지 가로지르므로 캔버스 밖에 둔다.
         다만 y 는 캔버스와 같은 식으로 푼다 - 점 5개 중 둘이 "선과 선이 만나는 자리"라
         좌표계가 다르면 어긋난다. 실제로 섹션 픽셀 고정이던 동안 36~67 어긋나 있었다
         (2026-09-26). -->
    <div
      v-for="y in [247, 460]"
      :key="`h-${y}`"
      aria-hidden
      class="pointer-events-none absolute left-0 h-0 w-full"
      :style="[{ top: `${(y - TOP_TRIM) * k}px` }, ambient]"
    >
      <div class="absolute inset-[-0.5px_0]">
        <img alt="" class="block max-w-none size-full" :src="imgVector17" />
      </div>
    </div>

    <!-- 구조 층. 1920 캔버스를 통째로 줄여 기준선(1570)에 맞춘다.
         폭을 좁히면 안 된다 - 안쪽 좌표가 X() 퍼센트라 가로만 줄어 카드가 납작해진다. -->
    <div
      class="absolute left-1/2 origin-top"
      :style="{
        top: `${-TOP_TRIM * k}px`,
        width: `${DESIGN_W}px`,
        height: `${H}px`,
        transform: `translateX(-50%) scale(${k})`,
      }"
    >
      <!-- 세로선 3개 -->
      <div
        v-for="[x, beat, src] in VLINES"
        :key="`v-${x}`"
        aria-hidden
        class="absolute top-0 flex w-0 items-center justify-center"
        :style="[{ left: X(x), height: `${H}px` }, build(beat)]"
      >
        <div class="flex-none rotate-90">
          <div class="relative h-0" :style="{ width: `${H}px` }">
            <div class="absolute inset-[-0.5px_0]">
              <img alt="" class="block max-w-none size-full" :src="src" />
            </div>
          </div>
        </div>
      </div>

      <!-- 교차점 5개. 선과 선, 선과 카드 윗변이 만나는 자리다. -->
      <div
        v-for="[x, y, beat] in DOTS"
        :key="`d-${x}-${y}`"
        aria-hidden
        class="-translate-x-1/2 absolute size-[10px]"
        :style="[{ left: X(x), top: `${y}px` }, beat === null ? ambient : build(beat)]"
      >
        <div class="absolute inset-[-250%_-220%_-190%_-220%]">
          <img alt="" class="block max-w-none size-full" :src="imgEllipse88" />
        </div>
      </div>

      <!-- 카드 3장. 아래끝이 모두 1116 에서 맞는다. -->
      <div
        class="absolute rounded-tl-[30px] rounded-tr-[30px] border-l-3 border-r-3 border-t-3 border-solid border-[#ffd680] backdrop-blur-[25px]"
        :style="[{ left: X(197), top: '809px', width: X(510), height: '307px' }, CARD1_BG, build(0, 364)]"
        data-node-id="391:296"
      />
      <div
        class="absolute rounded-tl-[30px] rounded-tr-[30px] border-l-3 border-r-3 border-t-3 border-solid border-[#ffd680] backdrop-blur-[25px]"
        :style="[{ left: X(704), top: '694px', width: X(511), height: '422px' }, CARD2_BG, build(2, 479)]"
        data-node-id="391:295"
      />
      <div
        class="absolute overflow-hidden rounded-tl-[30px] rounded-tr-[30px] border-l-3 border-r-3 border-t-3 border-solid border-[#ffd680]"
        :style="[{ left: X(1212), top: '381px', width: X(511), height: '735px' }, build(4, 792)]"
        data-node-id="391:310"
      >
        <img alt="" class="absolute inset-0 block size-full max-w-none object-bottom" :src="imgRectangle240649922" />
        <div class="absolute inset-0" :style="CARD3_GLOW" />
      </div>
      <div
        class="absolute rounded-tl-[20px] rounded-tr-[20px] border-l-3 border-r-3 border-t-3 border-solid border-white opacity-40"
        :style="[{ left: X(1222), top: '390px', width: X(492), height: '726px' }, build(4, 792)]"
        data-node-id="391:311"
      />

      <!-- 카드1 내용 -->
      <div class="absolute" :style="[{ left: X(240), top: '852px', width: X(424) }, build(0, 364)]" data-node-id="391:299">
        <p class="vision-c1-label">국내 금·은 시장 규모</p>
        <p class="vision-c1-note">2025년 ~ 2026년</p>
      </div>
      <!-- 숫자와 단위는 붙여 놓는다. 간격은 1920 에서 잰 원본 그대로(6.5). -->
      <div class="absolute flex items-start" :style="[{ left: X(240), top: '982px' }, build(0, 364)]" data-node-id="391:303">
        <p class="vision-num vision-num-70">20</p>
        <p class="vision-num vision-num-60 vision-unit-60">조</p>
      </div>

      <!-- 카드2 내용 -->
      <div
        class="absolute flex items-stretch"
        :style="[{ left: X(707), top: '697px', width: X(505), height: '90px' }, build(2, 479)]"
        data-node-id="391:326"
      >
        <div class="vision-tag vision-tag-rwa rounded-tl-[30px]">RWA</div>
        <div class="vision-tag vision-tag-sto rounded-tr-[30px]">STO</div>
      </div>
      <p class="vision-c2-sub absolute" :style="[{ left: X(747), top: '817px', width: X(426) }, build(2, 479)]" data-node-id="391:309">
        Vision 2027, RWA·STO가 이끄는 자산 생태계의 대전환
      </p>
      <p class="vision-num vision-num-30 absolute" :style="[{ left: X(747), top: '954px' }, build(2, 479)]" data-node-id="391:308">Vision</p>
      <p class="vision-num vision-num-70 absolute" :style="[{ left: X(747), top: '982px' }, build(2, 479)]" data-node-id="391:307">2027</p>

      <!-- 카드3 내용 -->
      <div class="absolute" :style="[{ left: X(1255), top: '434px', width: X(425) }, build(4, 792)]" data-node-id="391:312">
        <p class="vision-c3-note">2030년 예상</p>
        <p class="vision-c3-label">국내 RWA 시장 규모</p>
        <div class="vision-c3-srcs">
          <template v-for="(s, i) in SOURCES" :key="s">
            <span v-if="i" aria-hidden class="block size-[3px] shrink-0">
              <img alt="" class="block size-full" :src="imgEllipse76" />
            </span>
            <span class="vision-c3-src">{{ s }}</span>
          </template>
        </div>
      </div>
      <div class="absolute flex items-start" :style="[{ left: X(1255), top: '954px' }, build(4, 792)]" data-node-id="391:322">
        <p class="vision-num vision-num-100">367</p>
        <p class="vision-num vision-num-90 vision-unit-90">조</p>
      </div>

      <!-- 카드2 → 카드3 을 잇는 금색 화살표 -->
      <div
        aria-hidden
        class="absolute"
        :style="[{ left: X(782), top: '624px', width: X(358), height: '41.568px' }, build(3)]"
        data-node-id="391:340"
      >
        <img alt="" class="absolute inset-0 block size-full max-w-none" :src="imgUnion" />
      </div>
    </div>

    <!-- 머리말. 그림이 아니라 글이라 캔버스 밖 흐름에 둔다(2026-09-26).
         캔버스 안에 있던 동안은 그 scale(0.818)에 같이 줄어 제목이 다른 섹션보다
         18% 작고 98 안으로 들어가 있었다. 여기서는 다른 섹션과 같은 상자
         (1530 + 좌우 20)에, 같은 크기로 선다.
         상자를 줄임(--fit)으로 나누는 것도 미디어·거래소개와 같은 이유다 - 섹션이
         줄어드는 해상도에서 좌우 여백까지 같이 줄면 글이 안쪽으로 밀린다. -->
    <div
      class="relative mx-auto flex w-full flex-col items-start"
      :style="{
        maxWidth: 'calc(1530px / var(--fit, 1))',
        paddingLeft: 'calc(20px / var(--fit, 1))',
        paddingRight: 'calc(20px / var(--fit, 1))',
      }"
    >
      <span class="vision-eyebrow" :style="head(0)" data-node-id="391:335">
        {{ 'Beyond Gold & Silver' }}
      </span>
      <p class="vision-title vision-head-title" :style="head(0)" data-node-id="391:333">
        금·은 시장은 새로운 자산 생태계로
      </p>
      <p class="vision-title" :style="head(0)" data-node-id="391:338">
        변화의 시대를 맞이하고 있습니다.
      </p>
      <div class="vision-lead vision-head-lead" :style="head(1)" data-node-id="391:337">
        <p>{{ 'RWA·STO 시장의 성장과 함께 실물 ' }}</p>
        <p>{{ '금·은 자산은 거래를 넘어 자산 관리와 ' }}</p>
        <p>운용의 영역으로 연결됩니다.</p>
      </div>
    </div>
  </section>
  </div>
</template>

<style scoped>
/* 글자는 전부 원본 px 고정이다. 가로만 반응하므로 창 폭을 탈 이유가 없다. */
.vision-eyebrow {
  display: inline-flex;
  align-items: center;
  border: 1px solid #a66e08;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #a66e08;
  white-space: nowrap;
}

.vision-title {
  margin: 0;
  /* 여섯 섹션 제목을 50 으로 맞췄다(2026-09-26 요청). 원본은 56 이었다.
     행간 66px 은 원본 좌표(222 / 288)의 간격 그대로다.
     --fit 으로 나누는 건 섹션 줄임을 되돌려 어느 해상도에서나 50 으로 보이게 하는
     것이다. 2026-09-26 에 머리말을 캔버스 밖으로 꺼내기 전까지는 캔버스의
     scale(0.818)까지 겹쳐 실제로는 41 로 그려지고 있었다. */
  font-size: calc(50px / var(--fit, 1));
  font-weight: 700;
  line-height: 66px;
  color: #000;
  white-space: nowrap;
}
/* 머리말 사이 간격. 원본 좌표 그대로다 - 눈썹이 y150~194, 제목 첫 줄이 y222,
   본문이 y394 였고 제목 두 줄은 행간 66 으로 붙는다.
   줄임(--fit)으로 나누는 건 제목 크기와 같은 이유다(어느 해상도에서나 같은 간격). */
.vision-head-title {
  margin-top: calc(28px / var(--fit, 1));
}
.vision-head-lead {
  margin-top: calc(40px / var(--fit, 1));
}

/* 데스크톱 홈은 768 부터 그려진다. 그 언저리에서는 왼쪽 여백이 줄어도
   제목(761px)이 창을 넘는다. 1280 이상에는 영향이 없다. */
.vision-lead p {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  color: #000;
  opacity: 0.5;
  white-space: pre;
}

.vision-c1-label {
  margin: 0;
  font-size: 30px;
  font-weight: 600;
  line-height: 38px;
  color: #000;
  word-break: keep-all;
}
.vision-c1-note {
  margin: 5px 0 0;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  color: #000;
  word-break: keep-all;
}

.vision-c2-sub {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
  color: #000;
  word-break: keep-all;
}

.vision-c3-note {
  margin: 0;
  font-size: 25px;
  font-weight: 600;
  line-height: 32px;
  color: #000;
  word-break: keep-all;
}
.vision-c3-label {
  margin: 5px 0 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 48px;
  color: #000;
  word-break: keep-all;
}
.vision-c3-srcs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}
.vision-c3-src {
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
  color: #000;
  white-space: nowrap;
}

/* 큰 숫자들. 원본은 leading:normal 에 tracking 이 붙어 있다. */
.vision-num {
  margin: 0;
  font-weight: 700;
  line-height: normal;
  color: #000;
  white-space: nowrap;
}
.vision-num-100 {
  font-size: 100px;
  letter-spacing: -1px;
}
.vision-num-90 {
  font-size: 90px;
}
.vision-num-70 {
  font-size: 70px;
  letter-spacing: -0.7px;
}
.vision-num-60 {
  font-size: 60px;
}
.vision-num-30 {
  font-size: 30px;
}

/* 단위(조)는 숫자 바로 뒤에 붙인다. 1920 에서 잰 원본 간격이 6.5 / 8.3 이고,
   세로로는 원본이 10 / 9 만큼 내려 앉아 있다.
   Tailwind 의 여백 클래스로는 안 된다 — 위 margin:0 이 scoped 가산점으로 이긴다. */
.vision-unit-60 {
  margin: 10px 0 0 6.5px;
}
.vision-unit-90 {
  margin: 9px 0 0 8.3px;
}
/* RWA / STO 두 칸. 원본은 폭 505 를 반씩 나눠 쓴다. */
.vision-tag {
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  align-items: center;
  justify-content: center;
  border-bottom: 5px solid;
  font-size: 32px;
  font-weight: 700;
  line-height: 23px;
  white-space: nowrap;
}
.vision-tag-rwa {
  border-bottom-color: #fead04;
  background-image: linear-gradient(to bottom, rgba(255, 206, 103, 0), #ffce67);
  color: #674100;
}
.vision-tag-sto {
  border-bottom-color: #57bfff;
  background-image: linear-gradient(to bottom, rgba(126, 189, 226, 0), #c0e0f5);
  color: #00588e;
}
</style>
